(() => {
  'use strict';

  if (window.__lcFavoritesInitialized) return;
  window.__lcFavoritesInitialized = true;

  const STORAGE_KEY = 'lighting-centre:favorites:v1';
  const MAX_FAVORITES = 100;
  const HANDLE_PATTERN = /^[a-z0-9][a-z0-9-]*$/;
  const config = window.LightingCentre || {};
  const rootPath = String(config.routes?.root || '/').replace(/\/?$/, '/');
  const collectionPath = `${rootPath}collections/all`;
  let memoryHandles = [];
  let storageEnabled = true;
  let refreshSequence = 0;
  let statusTimer;
  let syncScheduled = false;

  const normalizeHandle = (value) => {
    const handle = typeof value === 'string' ? value.trim() : '';
    return HANDLE_PATTERN.test(handle) ? handle : '';
  };

  const normalizeHandles = (value) => {
    if (!Array.isArray(value)) return [];
    return [...new Set(value.map(normalizeHandle).filter(Boolean))].slice(0, MAX_FAVORITES);
  };

  const readHandles = () => {
    if (!storageEnabled) return [...memoryHandles];
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      memoryHandles = normalizeHandles(stored ? JSON.parse(stored) : []);
    } catch (_error) {
      storageEnabled = false;
      memoryHandles = normalizeHandles(memoryHandles);
    }
    return [...memoryHandles];
  };

  const writeHandles = (handles) => {
    memoryHandles = normalizeHandles(handles);
    if (storageEnabled) {
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(memoryHandles));
      } catch (_error) {
        storageEnabled = false;
        // Private browsing and storage policies can disable localStorage. The in-memory
        // copy keeps favorites usable for the rest of the current page visit.
      }
    }
    return [...memoryHandles];
  };

  const itemLabel = (count) => `${count} ${count === 1 ? 'item' : 'items'}`;
  const favoriteLabel = (count) => `${count} ${count === 1 ? 'favorite' : 'favorites'}`;

  const announce = (message) => {
    const status = document.querySelector('[data-lc-status]');
    if (!status || !message) return;
    window.clearTimeout(statusTimer);
    status.textContent = message;
    status.classList.add('is-visible');
    statusTimer = window.setTimeout(() => status.classList.remove('is-visible'), 3200);
  };

  const syncInterface = () => {
    const handles = readHandles();
    const saved = new Set(handles);

    document.querySelectorAll('[data-lc-favorite-count]').forEach((count) => {
      count.textContent = String(handles.length);
      count.setAttribute('aria-label', favoriteLabel(handles.length));
    });

    document.querySelectorAll('[data-lc-favorite-open]').forEach((button) => {
      button.setAttribute('aria-label', `Favorites, ${itemLabel(handles.length)}`);
    });

    document.querySelectorAll('[data-lc-favorite-toggle]').forEach((button) => {
      const handle = normalizeHandle(button.dataset.lcFavoriteHandle);
      const title = button.dataset.lcFavoriteTitle || 'this product';
      const isSaved = Boolean(handle && saved.has(handle));
      const action = isSaved ? 'Remove' : 'Add';
      const label = `${action} ${title} ${isSaved ? 'from' : 'to'} favorites`;

      button.setAttribute('aria-pressed', String(isSaved));
      button.setAttribute('aria-label', label);
      button.setAttribute('title', isSaved ? 'Remove from favorites' : 'Add to favorites');
      button.classList.toggle('is-active', isSaved);

      const visibleLabel = button.querySelector('[data-lc-favorite-label]');
      if (visibleLabel) visibleLabel.textContent = label;
      const inactiveIcon = button.querySelector('[data-lc-favorite-icon-inactive]');
      const activeIcon = button.querySelector('[data-lc-favorite-icon-active]');
      if (inactiveIcon) inactiveIcon.hidden = isSaved;
      if (activeIcon) activeIcon.hidden = !isSaved;
    });
  };

  const scheduleSync = () => {
    if (syncScheduled) return;
    syncScheduled = true;
    window.requestAnimationFrame(() => {
      syncScheduled = false;
      syncInterface();
    });
  };

  const productPath = (handle, json = false) => `${rootPath}products/${encodeURIComponent(handle)}${json ? '.js' : ''}`;

  const makeElement = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  };

  const mapWithConcurrency = async (items, worker, limit = 6) => {
    const results = new Array(items.length);
    let nextIndex = 0;
    const run = async () => {
      while (nextIndex < items.length) {
        const index = nextIndex;
        nextIndex += 1;
        results[index] = await worker(items[index]);
      }
    };
    await Promise.all(Array.from({ length: Math.min(limit, items.length) }, run));
    return results;
  };

  const appendHiddenInput = (form, name, value) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = String(value);
    form.append(input);
  };

  const formatMoney = (cents) => {
    const amount = Number(cents);
    if (!Number.isFinite(amount)) return '';
    const currency = config.currency || 'CAD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-CA', {
        style: 'currency',
        currency
      }).format(amount / 100);
    } catch (_error) {
      return `$${(amount / 100).toFixed(2)} ${currency}`;
    }
  };

  const imageSource = (product) => {
    const featured = product?.featured_image || product?.images?.[0];
    const source = typeof featured === 'string' ? featured : featured?.src;
    if (!source) return '';
    try {
      const imageUrl = new URL(source, window.location.origin);
      imageUrl.searchParams.set('width', '240');
      return imageUrl.toString();
    } catch (_error) {
      return source;
    }
  };

  const verifyQuickAdd = async (handle) => {
    try {
      const response = await fetch(productPath(handle), {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' }
      });
      if (!response.ok) return false;
      const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
      const productRoot = [...documentFragment.querySelectorAll('[data-product-root]')].find(
        (element) => element.dataset.productHandle === handle
      );
      return Boolean(
        productRoot &&
        productRoot.dataset.purchaseSupported === 'true' &&
        productRoot.dataset.requiresConfirmation !== 'true'
      );
    } catch (_error) {
      return false;
    }
  };

  const fetchFavorite = async (handle) => {
    try {
      const response = await fetch(productPath(handle, true), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });

      if (response.status === 404 || response.status === 410) return { state: 'deleted', handle };
      if (!response.ok) return { state: 'error', handle };

      const product = await response.json();
      const variants = Array.isArray(product.variants) ? product.variants : [];
      const availableVariants = variants.filter((variant) => variant?.available);
      return { state: 'ready', handle, product, variants, availableVariants };
    } catch (_error) {
      return { state: 'error', handle };
    }
  };

  const makeEmptyState = (message = 'Use the heart on a product to save it here.') => {
    const empty = makeElement('div', 'lc-empty-state lc-favorite-empty');
    empty.append(makeElement('h3', 'lc-favorite-empty__title', 'No favorites yet'));
    empty.append(makeElement('p', 'lc-favorite-empty__text', message));
    const link = makeElement('a', 'lc-button lc-button--primary lc-favorite-empty__link', 'Continue shopping');
    link.href = collectionPath;
    empty.append(link);
    return empty;
  };

  const makeNotice = (message) => {
    const notice = makeElement('p', 'lc-product-form__error lc-favorite-drawer__notice', message);
    notice.setAttribute('role', 'status');
    return notice;
  };

  const makeUnavailableItem = (handle) => {
    const item = makeElement('li', 'lc-cart-item lc-favorite-item lc-favorite-item--unavailable');
    const placeholder = makeElement('span', 'lc-cart-item__image lc-favorite-item__image lc-favorite-item__image--unavailable', 'Image unavailable');
    placeholder.setAttribute('aria-hidden', 'true');
    const details = makeElement('div', 'lc-cart-item__details lc-favorite-item__details');
    details.append(makeElement('p', 'lc-cart-item__title lc-favorite-item__title', 'Unable to load this favorite'));
    details.append(makeElement('p', 'lc-cart-item__variant lc-favorite-item__handle', handle));
    const controls = makeElement('div', 'lc-cart-item__controls lc-favorite-item__controls');
    const retry = makeElement('button', 'lc-button lc-button--secondary lc-button--compact lc-favorite-retry', 'Try again');
    retry.type = 'button';
    retry.dataset.lcFavoriteRetry = '';
    const remove = makeElement('button', 'lc-text-button lc-favorite-remove', 'Remove');
    remove.type = 'button';
    remove.dataset.lcFavoriteRemove = '';
    remove.dataset.lcFavoriteHandle = handle;
    remove.setAttribute('aria-label', `Remove ${handle} from favorites`);
    controls.append(retry, remove);
    details.append(controls);
    item.append(placeholder, details);
    return item;
  };

  const makeProductItem = ({ handle, product, variants, availableVariants }) => {
    const item = makeElement('li', 'lc-cart-item lc-favorite-item');
    item.dataset.lcFavoriteItem = handle;
    const title = String(product.title || handle);
    const href = productPath(handle);
    const imageLink = makeElement('a', 'lc-cart-item__image lc-favorite-item__image');
    imageLink.href = href;
    imageLink.setAttribute('aria-label', title);

    const source = imageSource(product);
    if (source) {
      const image = document.createElement('img');
      image.src = source;
      image.alt = title;
      image.loading = 'lazy';
      image.decoding = 'async';
      image.width = 240;
      image.height = 240;
      imageLink.append(image);
    } else {
      imageLink.append(makeElement('span', 'lc-favorite-item__image-missing', 'Image unavailable'));
    }

    const details = makeElement('div', 'lc-cart-item__details lc-favorite-item__details');
    if (product.vendor) details.append(makeElement('p', 'lc-eyebrow lc-favorite-item__vendor', String(product.vendor)));
    const titleLink = makeElement('a', 'lc-cart-item__title lc-favorite-item__title', title);
    titleLink.href = href;
    details.append(titleLink);

    const price = formatMoney(product.price);
    if (price) {
      const priceLine = makeElement('p', 'lc-cart-item__price lc-favorite-item__price', price);
      const listedCents = Number(product.price);
      if (Number.isInteger(listedCents) && listedCents >= 0) {
        priceLine.dataset.lcdBasePrice = String(listedCents);
        priceLine.dataset.lcdPriceVaries = String(product.price_varies === true);
        priceLine.dataset.lcdCurrency = config.currency || 'CAD';
        if (product.id) priceLine.dataset.lcdProductId = String(product.id);
        priceLine.dataset.lcdProductHandle = handle;
        const listedVariant = product.price_varies === true ? null : (availableVariants[0] || variants[0]);
        if (listedVariant && Number(listedVariant.price) === listedCents) priceLine.dataset.lcdVariantId = String(listedVariant.id);
      }
      if (Number(product.compare_at_price) > Number(product.price)) {
        const compare = makeElement('s', 'lc-favorite-item__compare-price', formatMoney(product.compare_at_price));
        priceLine.prepend(compare, document.createTextNode(' '));
      }
      details.append(priceLine);
    }

    const soldOut = product.available === false || availableVariants.length === 0;
    const controls = makeElement('div', 'lc-cart-item__controls lc-favorite-item__controls');

    if (soldOut) {
      const soldOutButton = makeElement('button', 'lc-button lc-button--secondary lc-button--compact lc-favorite-item__action', 'Sold out');
      soldOutButton.type = 'button';
      soldOutButton.disabled = true;
      soldOutButton.setAttribute('aria-disabled', 'true');
      controls.append(soldOutButton);
    } else if (variants.length === 1 && availableVariants.length === 1) {
      const form = makeElement('form', 'lc-quick-add lc-favorite-quick-add');
      form.method = 'post';
      form.action = config.routes?.cartAdd || `${rootPath}cart/add`;
      form.dataset.lcFavoriteVerifyAdd = handle;
      appendHiddenInput(form, 'id', variants[0].id);
      appendHiddenInput(form, 'quantity', 1);
      const addButton = makeElement('button', 'lc-button lc-button--secondary lc-button--compact lc-favorite-item__action', 'Add to cart');
      addButton.type = 'button';
      addButton.dataset.lcFavoriteVerifyAddButton = '';
      const error = makeElement('div', 'lc-product-form__error lc-favorite-quick-add__error');
      error.hidden = true;
      error.setAttribute('role', 'alert');
      error.dataset.productError = '';
      form.append(addButton, error);
      controls.append(form);
    } else {
      const choose = makeElement('a', 'lc-button lc-button--secondary lc-button--compact lc-favorite-item__action', 'Choose options');
      choose.href = href;
      controls.append(choose);
    }

    const remove = makeElement('button', 'lc-text-button lc-favorite-remove', 'Remove');
    remove.type = 'button';
    remove.dataset.lcFavoriteRemove = '';
    remove.dataset.lcFavoriteHandle = handle;
    remove.setAttribute('aria-label', `Remove ${title} from favorites`);
    controls.append(remove);
    details.append(controls);
    item.append(imageLink, details);
    return item;
  };

  const refreshDrawer = async (drawer = document.querySelector('[data-lc-favorite-drawer]')) => {
    const content = drawer?.querySelector('[data-lc-favorite-content]');
    if (!drawer || !content) return;

    const sequence = ++refreshSequence;
    const handles = readHandles();
    content.setAttribute('aria-busy', 'true');

    if (!handles.length) {
      content.replaceChildren(makeEmptyState());
      content.removeAttribute('aria-busy');
      return;
    }

    const loading = makeElement('p', 'lc-favorite-loading', `Loading ${itemLabel(handles.length)}…`);
    loading.setAttribute('role', 'status');
    content.replaceChildren(loading);

    const results = await mapWithConcurrency(handles, fetchFavorite);
    if (sequence !== refreshSequence || !document.contains(content)) return;

    const deleted = results.filter((result) => result.state === 'deleted');
    if (deleted.length) {
      const deletedHandles = new Set(deleted.map((result) => result.handle));
      writeHandles(handles.filter((handle) => !deletedHandles.has(handle)));
      syncInterface();
    }

    const renderable = results.filter((result) => result.state !== 'deleted');
    if (!renderable.length) {
      const message = deleted.length
        ? 'Products that are no longer available were removed from your favorites.'
        : 'Use the heart on a product to save it here.';
      content.replaceChildren(makeEmptyState(message));
      content.removeAttribute('aria-busy');
      if (deleted.length) announce('Unavailable products were removed from favorites.');
      return;
    }

    const fragment = document.createDocumentFragment();
    if (deleted.length) {
      fragment.append(makeNotice(`${deleted.length} unavailable ${deleted.length === 1 ? 'product was' : 'products were'} removed from your favorites.`));
    }
    const list = makeElement('ul', 'lc-cart-items lc-favorite-list');
    list.setAttribute('role', 'list');
    renderable.forEach((result) => {
      list.append(result.state === 'ready' ? makeProductItem(result) : makeUnavailableItem(result.handle));
    });
    fragment.append(list);
    content.replaceChildren(fragment);
    content.removeAttribute('aria-busy');
    if (deleted.length) announce('Unavailable products were removed from favorites.');
    try {
      window.LCDPromoPrices?.refresh(content);
    } catch (_error) {
      // Optional promo display must never interrupt the existing favorites behavior.
    }
  };

  const toggleFavorite = (button) => {
    const handle = normalizeHandle(button.dataset.lcFavoriteHandle);
    if (!handle) return;
    const title = button.dataset.lcFavoriteTitle || 'Product';
    const handles = readHandles();
    const existingIndex = handles.indexOf(handle);

    if (existingIndex >= 0) {
      handles.splice(existingIndex, 1);
      writeHandles(handles);
      announce(`${title} removed from favorites.`);
    } else {
      handles.unshift(handle);
      writeHandles(handles);
      announce(`${title} added to favorites.`);
    }
    syncInterface();
  };

  document.addEventListener('click', (event) => {
    const jump = event.target.closest('[data-lc-favorite-jump]');
    if (jump) {
      event.preventDefault();
      const currentDrawer = jump.closest('[data-drawer]');
      currentDrawer?.querySelector('[data-drawer-close]')?.click();
      window.setTimeout(() => document.querySelector('[data-lc-favorite-open]')?.click(), 270);
      return;
    }

    const toggle = event.target.closest('[data-lc-favorite-toggle]');
    if (toggle) {
      event.preventDefault();
      event.stopPropagation();
      toggleFavorite(toggle);
      return;
    }

    const verifiedAdd = event.target.closest('[data-lc-favorite-verify-add-button]');
    if (verifiedAdd) {
      event.preventDefault();
      const form = verifiedAdd.closest('[data-lc-favorite-verify-add]');
      const handle = normalizeHandle(form?.dataset.lcFavoriteVerifyAdd);
      if (!form || !handle || verifiedAdd.disabled) return;
      verifiedAdd.disabled = true;
      verifiedAdd.setAttribute('aria-busy', 'true');
      const originalLabel = verifiedAdd.textContent;
      verifiedAdd.textContent = 'Checking availability…';
      verifyQuickAdd(handle).then((allowed) => {
        if (!allowed) {
          window.location.assign(productPath(handle));
          return;
        }
        form.dataset.quickAdd = '';
        verifiedAdd.type = 'submit';
        verifiedAdd.textContent = originalLabel;
        verifiedAdd.disabled = false;
        verifiedAdd.removeAttribute('aria-busy');
        form.requestSubmit(verifiedAdd);
      });
      return;
    }

    const remove = event.target.closest('[data-lc-favorite-remove]');
    if (remove) {
      event.preventDefault();
      const handle = normalizeHandle(remove.dataset.lcFavoriteHandle);
      if (!handle) return;
      writeHandles(readHandles().filter((savedHandle) => savedHandle !== handle));
      syncInterface();
      announce('Product removed from favorites.');
      refreshDrawer(remove.closest('[data-lc-favorite-drawer]'));
      return;
    }

    const retry = event.target.closest('[data-lc-favorite-retry]');
    if (retry) {
      event.preventDefault();
      refreshDrawer(retry.closest('[data-lc-favorite-drawer]'));
      return;
    }

    const opener = event.target.closest('[data-lc-favorite-open]');
    if (opener) {
      const drawerId = opener.getAttribute('aria-controls') || opener.dataset.drawerOpen;
      refreshDrawer(document.getElementById(drawerId));
    }
  });

  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY) return;
    syncInterface();
    const openDrawer = document.querySelector('[data-lc-favorite-drawer].is-open');
    if (openDrawer) refreshDrawer(openDrawer);
  });

  document.addEventListener('shopify:section:load', scheduleSync);

  const observer = new MutationObserver((mutations) => {
    const hasNewFavoriteInterface = mutations.some((mutation) =>
      [...mutation.addedNodes].some(
        (node) => node.nodeType === Node.ELEMENT_NODE &&
          (node.matches?.('[data-lc-favorite-toggle], [data-lc-favorite-count], [data-lc-favorite-open]') ||
            node.querySelector?.('[data-lc-favorite-toggle], [data-lc-favorite-count], [data-lc-favorite-open]'))
      )
    );
    if (hasNewFavoriteInterface) scheduleSync();
  });

  syncInterface();
  observer.observe(document.body, { childList: true, subtree: true });
})();
